import React from "react";
import {
  Box,
  Typography,
  Chip,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import HPButton from "@components/common/Button";
import type { ScorecardBrand } from "@models/Scorecard";
import { getOrderedBrands } from "@utils/scorecard-column-utils";
import BrandColumnListItem from "./BrandColumnListItem";

interface ColumnCustomizationSectionProps {
  brands: ScorecardBrand[];
  columnOrder: string[];
  hiddenBrandIds: string[];
  onToggleVisibility: (id: string) => void;
  onShowAll: () => void;
  onHideAll: () => void;
  onReorder: (fromIndex: number, toIndex: number) => void;
  onResetOrder: () => void;
}

/** Lets the researcher reorder and show/hide every selected brand column —
 *  Primary included, it can move to any position or be hidden like any
 *  Competitor. Mirrors Crosstab's Column Customization list
 *  (CrosstabCustomizationPanel): drag handle, eye toggle, label,
 *  visible-count chip, Show All/Hide All, plus a Reset Order control. */
const ColumnCustomizationSection: React.FC<ColumnCustomizationSectionProps> = ({
  brands,
  columnOrder,
  hiddenBrandIds,
  onToggleVisibility,
  onShowAll,
  onHideAll,
  onReorder,
  onResetOrder,
}) => {
  const orderedBrands = getOrderedBrands(brands, columnOrder);
  const orderedIds = orderedBrands.map((b) => b.id);
  const visibleCount = orderedBrands.filter((b) => !hiddenBrandIds.includes(b.id)).length;

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const fromIndex = orderedIds.indexOf(active.id as string);
    const toIndex = orderedIds.indexOf(over.id as string);
    if (fromIndex !== -1 && toIndex !== -1) onReorder(fromIndex, toIndex);
  };

  if (orderedBrands.length === 0) return null;

  return (
    <Accordion defaultExpanded sx={{ boxShadow: "none", border: "1px solid #eee" }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, width: "100%" }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Column Customization
          </Typography>
          <Chip
            label={`${visibleCount}/${orderedBrands.length} visible`}
            size="small"
            color={visibleCount === orderedBrands.length ? "default" : "primary"}
          />
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
          <Box sx={{ display: "flex", gap: 1 }}>
            <HPButton
              size="small"
              variant="outlined"
              onClick={onShowAll}
              disabled={visibleCount === orderedBrands.length}
            >
              Show All
            </HPButton>
            <HPButton
              size="small"
              variant="outlined"
              onClick={onHideAll}
              disabled={visibleCount <= 1}
            >
              Hide All
            </HPButton>
            <HPButton size="small" variant="text" onClick={onResetOrder}>
              Reset Order
            </HPButton>
          </Box>

          <Divider />

          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <SortableContext items={orderedIds} strategy={verticalListSortingStrategy}>
                {orderedBrands.map((brand) => {
                  const isHidden = hiddenBrandIds.includes(brand.id);
                  return (
                    <BrandColumnListItem
                      key={brand.id}
                      brand={brand}
                      isHidden={isHidden}
                      isLastVisible={!isHidden && visibleCount === 1}
                      onToggleVisibility={onToggleVisibility}
                    />
                  );
                })}
              </SortableContext>
            </Box>
          </DndContext>
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};

export default ColumnCustomizationSection;
